/*
 * reports-b shell.
 *
 * Top-level container that mounts every page in the prototype. Owns the
 * navigation stack (breadcrumb trail of page ids + per-crumb scope
 * context), the global period selector, the shared scope filters
 * (department / location / country / etc.), the nav-lock topbar and the
 * three customise drawers (Summary / Sims / Courses). All page
 * transitions go through go() (replace stack) or drill() (append to
 * stack); pages never call the router directly.
 *
 * Calculations: none. Stack -> scope mapping accumulates scope dimensions
 * by walking the breadcrumb trail; scope object shape is
 *   { department?: string[], location?: string[], country?: string[],
 *     city?: string[], state?: string[], company?: string[],
 *     manager?: string[], user?: string }.
 *
 * localStorage keys:
 *   - rbx-nav-locked: '0' | '1' boolean sticky topbar.
 *   - everything else lives in feature-specific modules (featureFlags,
 *     useComparator, useWidgetVisibility, UsersPage, FlaggedSimsPage).
 *
 * sessionStorage keys (NBD-25):
 *   - rbx-report-filters:<userId>:<tenancyId>: shared date window, scope
 *     filters, exclude-disabled toggle and last tab. Cleared on logout.
 *
 * Postgres mapping:
 *   - navigation stack and scope are session UI state; nothing persists
 *     beyond the browser session.
 *   - nav-lock preference -> `user_preferences` (user_id, 'nav_locked').
 *   - inlined CSS token block should move to the global Boxphish theme
 *     once the brand harmonisation lands; right now reports-b ships its
 *     own scoped `.rbx-root` palette so it can iterate independently.
 *
 * Integration hook:
 *   - no API surface here. Pages mount RTK Query hooks individually so
 *     this shell stays declarative.
 *   - the manual-refresh button should invalidate all reports-b cache
 *     tags via `dispatch(riskApi.util.invalidateTags(['Risk']))` etc.
 */
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { motion } from 'motion/react';
import Breadcrumb from './components/Breadcrumb';
import NavBar from './components/NavBar';
import FilterBar from './components/FilterBar';
import ActiveFilters from './components/ActiveFilters';
import WidgetState, { HydratingContext } from './components/WidgetState';
import MetaNav from './components/MetaNav';
import Drawer from './components/Drawer';
import SummaryPage from './components/summary/SummaryPage';
import DeptPage from './components/DeptPage';
import SimsPage from './components/SimsPage';
import UsersPage from './components/UsersPage';
import CoursesPage from './components/CoursesPage';
import SimDetailPage from './components/SimDetailPage';
import AllSimsPage from './components/AllSimsPage';
import UserDetailPage from './components/UserDetailPage';
import MetaDetailPage from './components/MetaDetailPage';
import SendHistoryPage from './components/SendHistoryPage';
import CourseSendHistoryPage from './components/CourseSendHistoryPage';
import AllCoursesPage from './components/AllCoursesPage';
import CourseDetailPage from './components/CourseDetailPage';
import MetaListPage from './components/MetaListPage';
import OutstandingUsersPage from './components/OutstandingUsersPage';
import RiskMethodologyPage from './components/RiskMethodologyPage';
import FlaggedSimsPage from './components/FlaggedSimsPage';
import DwmPage from './components/DwmPage';
import ExportBuilderPage from './components/ExportBuilderPage';
import ScheduledExportsPage from './components/ScheduledExportsPage';
import DataTablesPage from './components/DataTablesPage';
import ExportHistoryPage from './components/ExportHistoryPage';
import FactorDetailPage from './components/FactorDetailPage';
import PerformancePage from './components/PerformancePage';
import { PERFORMANCE_PAGE, parsePerformanceContext } from './performanceConfig';
import { FEATURES, subscribeFeatures } from './featureFlags';
import { SIMS, USERS, COURSES, DATA_EPOCH, hydrateData, applyRiskScores, setAuthoredCourseTags, buildDimOptions } from './data';
import { FilterChip } from './components/MetaNav';
import DisabledFilterChip from './components/DisabledFilterChip';
import { setExcludeDisabled, useExcludeDisabled, IgnoreExcludeDisabled } from './disabledFilter';
import { ReportFiltersContext, DEFAULT_PERIOD, contextKey, restoreDateWindow, restoreFilterContexts, restoreTab, serialiseReportFilters } from './reportFilterStore';
import { readReportFilterSession, writeReportFilterSession } from 'utils/reportFilterSession';
import { useDescendantTenancyState, defaultTenancyIds, defaultTenancyPending } from './tenancyScope';
import { selectsEveryOption } from './serverScope';
import { recomputeRiskModel, USER_SCORES } from './riskScore';
import useDashboardData, { PERIOD_TO_RANGE, browserEventsStart, selectDashboardBusy } from './hooks/useDashboardData';
import { setServerSimTags } from './tagAnalytics';
import { useGetSimulationsQuery } from 'store/services/simulation-service';
import { useGetUserByIdQuery } from 'store/services/user-service';
import { isScopedReportingUser, buildScopeBanner, canSliceWithinTenancy } from './scopeAccess';
import { ACTIVE_FILTER_PAGES, COURSE_CHIP_PAGES, DATE_BEARING_PAGES, SCOPED_PAGES, SIM_CHIP_PAGES } from './filterPages';
import { metaListContext, metaListRunStart } from './metaListNav';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

// Maps a context type coming from drill() to the DeptPage group selector value
const CONTEXT_TYPE_TO_GROUP = {
    department: 'Department',
    location:   'Location',
    city:       'City',
    country:    'Country',
    company:    'Company',
    state:      'State',
};

// Scope-dimension context types that must survive a top-level tab switch.
// go() rebuilds the stack from scratch, so these crumbs are carried across
// so the user's tenancy / department / location / ... filters don't silently
// vanish when they click another tab. Page-specific filters (sim / course /
// tag) are intentionally NOT carried: they have no meaning on a sibling tab.
const SCOPE_DIM_TYPES = ['tenancy', 'department', 'location', 'city', 'country', 'state', 'company', 'user'];
const FILTER_TYPES = [...SCOPE_DIM_TYPES, 'sim', 'course'];
const filterTypesFor = (pageId) => (pageId === 'summary' ? ['tenancy'] : SCOPE_DIM_TYPES);
const toFilterEntries = (filters, anchor) => filters.map((context) => ({ id: anchor.id, label: anchor.label, context, filter: true }));
const carryFilters = (from, onto, target) => {
    const allowed = filterTypesFor(target.id);
    const seen = new Set(onto.filter((e) => e.context).map((e) => contextKey(e.context)));
    const carried = [];
    from.forEach((e) => {
        if (!e.filter || !e.context || !allowed.includes(e.context.type) || seen.has(contextKey(e.context))) return;
        seen.add(contextKey(e.context));
        carried.push(e.context);
    });
    return toFilterEntries(carried, target);
};
const SHARED_AREA_IDS = new Set(['sims', 'courses', 'users', 'dwm', 'flagged-sims', 'export-builder', 'export-scheduled', 'export-tables', 'export-history']);
const RISK_ROOT = { id: 'summary', label: 'Risk' };
const isSharedArea = (stack) => stack.some((e) => SHARED_AREA_IDS.has(e.id));
const sharedTabOf = (stack) => stack.find((e) => SHARED_AREA_IDS.has(e.id)) || null;
const sharedFiltersOf = (stack) => {
    const seen = new Set();
    const filters = [];
    stack.forEach((e, i) => {
        if (i === 0 || !e.context || !SCOPE_DIM_TYPES.includes(e.context.type)) return;
        if (e.id === 'user-detail' && e.context.type === 'user') return;
        if (seen.has(contextKey(e.context))) return;
        seen.add(contextKey(e.context));
        filters.push({ type: e.context.type, value: e.context.value });
    });
    return filters;
};
const buildStack = (tab, filters, context = null) => {
    const anchor = tab || RISK_ROOT;
    const allowed = filterTypesFor(anchor.id);
    const entries = toFilterEntries(filters.filter((c) => allowed.includes(c.type)), anchor);
    return tab ? [RISK_ROOT, ...entries, { id: tab.id, label: tab.label, context }] : [RISK_ROOT, ...entries];
};
const withSharedFilters = (next, prevShared) => ({
    ...next,
    sharedFilters: isSharedArea(next.stack) ? sharedFiltersOf(next.stack) : (next.sharedFilters || prevShared),
});
const restoreShellState = (userId, tenancyId) => {
    const saved = readReportFilterSession(userId, tenancyId);
    return {
        hasSaved: !!saved,
        ...restoreDateWindow(saved),
        filters: restoreFilterContexts(saved, SCOPE_DIM_TYPES),
        tab: restoreTab(saved, SHARED_AREA_IDS),
        excludeDisabled: Boolean(saved && saved.excludeDisabled),
    };
};
// Single-item / drill detail pages: the Tenancy chip is hidden here. On the user / sim /
// course detail pages a persisted tenancy scope is also stripped before their queries run;
// factor-detail and meta-detail are aggregates, so they keep the tenancy scope.
const DRILL_PAGES = new Set(['sim-detail', 'course-detail', 'user-detail', 'factor-detail', 'meta-detail']);
// BD-1016: pages whose data does not come from /dashboard/data (or that build their own
// population filter, like the export builder) don't get the exclude-disabled chip.
// Pages that render nothing until the browser data has hydrated; they get a page
// skeleton meanwhile. Everything else renders straight away under widget skeletons,
// so its server queries start alongside the hydration.
const WAITS_FOR_DATA = new Set(['users', 'all-sims-table', 'all-courses-table', 'meta-detail', 'send-history', 'course-send-log', 'dwm', 'export-builder', 'export-scheduled', 'export-tables', 'export-history', 'meta-list']);
// BD-1245: the Risk page ('summary') and factor detail too, since risk figures ignore the toggle.
const NO_DISABLED_CHIP_PAGES = new Set(['summary', 'factor-detail', 'dwm', 'risk-methodology', 'export-builder', 'export-history', 'export-scheduled', 'export-tables']);
import { Box, IconButton, Tooltip, Button, ThemeProvider, createTheme, useTheme } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useThemeContext } from 'themes/ThemeContext';
import useWidgetVisibility from './useWidgetVisibility';
import CommandPalette from './components/CommandPalette';
import UserFilterChip from './components/UserFilterChip';
import WidgetPanel from './components/WidgetPanel';
import TuneIcon from '@mui/icons-material/Tune';

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');

.rbx-root {
  font-family: 'Inter', sans-serif;
  min-height: 100%;
}
.rbx-root *,
.rbx-root *::before,
.rbx-root *::after {
  box-sizing: border-box;
}

/* Scoped token forwarding via class-level custom props */
.rbx-root {
  --rbx-bg: #F8FAFC;
  --rbx-surface: #FFFFFF;
  --rbx-surface2: #F1F5F9;
  --rbx-border: rgba(0,0,0,.09);
  --rbx-border2: rgba(0,0,0,.14);
  --rbx-text: #111418;
  --rbx-muted: #6B7591;
  --rbx-dim: #A0A8B8;
  --rbx-faint: #D1D9E0;
  --rbx-red: #FF5252;
  --rbx-red-bg: #FFEBEE;
  --rbx-red-text: #E53935;
  --rbx-green: #66BB6A;
  --rbx-green-bg: #E8F5E9;
  --rbx-green-text: #388E3C;
  --rbx-amber: #FFCA28;
  --rbx-amber-bg: #FFF8E1;
  --rbx-amber-text: #C68A00;
  --rbx-teal: #00BFA5;
  --rbx-chart-grid: rgba(0,0,0,.05);
  --rbx-chart-tick: #A0A8B8;
  --rbx-risk-track: rgba(0,0,0,.08);
  --rbx-bar-track: #E8ECF0;

  /* forward to short names used in components */
  --bg: var(--rbx-bg);
  --surface: var(--rbx-surface);
  --surface2: var(--rbx-surface2);
  --border: var(--rbx-border);
  --border2: var(--rbx-border2);
  --text: var(--rbx-text);
  --muted: var(--rbx-muted);
  --dim: var(--rbx-dim);
  --faint: var(--rbx-faint);
  --red: var(--rbx-red);
  --red-bg: var(--rbx-red-bg);
  --red-text: var(--rbx-red-text);
  --green: var(--rbx-green);
  --green-bg: var(--rbx-green-bg);
  --green-text: var(--rbx-green-text);
  --amber: var(--rbx-amber);
  --amber-bg: var(--rbx-amber-bg);
  --amber-text: var(--rbx-amber-text);
  --teal: var(--rbx-teal);
  --chart-grid: var(--rbx-chart-grid);
  --chart-tick: var(--rbx-chart-tick);
  --risk-track: var(--rbx-risk-track);
  --bar-track: var(--rbx-bar-track);
}

.rbx-root[data-theme="dark"] {
  --rbx-bg: #27282A;
  --rbx-surface: #333537;
  --rbx-surface2: #3A3C40;
  --rbx-border: rgba(255,255,255,.15);
  --rbx-border2: rgba(255,255,255,.22);
  --rbx-text: #E8EAED;
  --rbx-muted: #8A919F;
  --rbx-dim: #5A6170;
  --rbx-faint: #3A3C40;
  --rbx-red: #FF5252;
  --rbx-red-bg: rgba(255,82,82,.22);
  --rbx-red-text: #FFAFAF;
  --rbx-green: #66BB6A;
  --rbx-green-bg: rgba(102,187,106,.18);
  --rbx-green-text: #B7E4BA;
  --rbx-amber: #FFCA28;
  --rbx-amber-bg: rgba(255,202,40,.2);
  --rbx-amber-text: #FFE9A8;
  --rbx-teal: #00BFA5;
  --rbx-chart-grid: rgba(255,255,255,.05);
  --rbx-chart-tick: #5A6170;
  --rbx-risk-track: rgba(255,255,255,.08);
  --rbx-bar-track: #3A3C40;

  --bg: var(--rbx-bg);
  --surface: var(--rbx-surface);
  --surface2: var(--rbx-surface2);
  --border: var(--rbx-border);
  --border2: var(--rbx-border2);
  --text: var(--rbx-text);
  --muted: var(--rbx-muted);
  --dim: var(--rbx-dim);
  --faint: var(--rbx-faint);
  --red: var(--rbx-red);
  --red-bg: var(--rbx-red-bg);
  --red-text: var(--rbx-red-text);
  --green: var(--rbx-green);
  --green-bg: var(--rbx-green-bg);
  --green-text: var(--rbx-green-text);
  --amber: var(--rbx-amber);
  --amber-bg: var(--rbx-amber-bg);
  --amber-text: var(--rbx-amber-text);
  --teal: var(--rbx-teal);
  --chart-grid: var(--rbx-chart-grid);
  --chart-tick: var(--rbx-chart-tick);
  --risk-track: var(--rbx-risk-track);
  --bar-track: var(--rbx-bar-track);
}

.topbar { display: flex; align-items: center; justify-content: space-between; padding: 10px 20px; }
.bc { display: flex; align-items: center; gap: 5px; font-size: 11px; }
.bc a { color: var(--teal); cursor: pointer; text-decoration: none; }
.bc a:hover { text-decoration: underline; }
.bc-cur { color: var(--muted); font-weight: 500; }
.bc-sep { color: var(--faint); }
.topbar-right { display: flex; align-items: center; gap: 10px; }
.lchip { font-size: 10px; color: var(--dim); display: flex; align-items: center; gap: 4px; }
.ldot { width: 6px; height: 6px; border-radius: 50%; background: var(--green); flex-shrink: 0; }
.tsel { font-size: 11px; font-weight: 500; padding: 4px 22px 4px 8px; border-radius: 6px; border: 1px solid var(--border2); background: var(--surface); color: var(--muted); font-family: 'Inter', sans-serif; cursor: pointer; appearance: none; width: 150px; background-image: url("data:image/svg+xml,%3Csvg width='7' height='4' viewBox='0 0 7 4' fill='none'%3E%3Cpath d='M1 1l2.5 2.5L6 1' stroke='%236B7591' stroke-width='1' stroke-linecap='round'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 7px center; transition: all .2s; }
.theme-btn { font-size: 11px; font-weight: 500; padding: 4px 10px; border-radius: 6px; border: 1px solid var(--border2); background: var(--surface); color: var(--muted); font-family: 'Inter', sans-serif; cursor: pointer; transition: all .15s; }
.theme-btn:hover { color: var(--text); border-color: var(--dim); }

.filterbar { display: flex; align-items: center; gap: 5px; padding: 2px 20px 10px; }
.tp { font-size: 11px; font-weight: 500; padding: 3px 10px; border-radius: 999px; border: 1px solid var(--border2); background: transparent; color: var(--muted); cursor: pointer; font-family: 'Inter', sans-serif; transition: all .15s; }
.tp.active { background: var(--text); color: var(--bg); border-color: var(--text); }
.tp:hover:not(.active) { color: var(--text); border-color: var(--dim); }

.tmpl-bar { display: flex; align-items: center; gap: 5px; margin-bottom: 12px; }
.tmpl-lbl { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: .06em; color: var(--dim); margin-right: 4px; }
.tbtn { font-size: 11px; font-weight: 500; padding: 3px 10px; border-radius: 6px; border: 1px solid var(--border2); background: transparent; color: var(--muted); cursor: pointer; font-family: 'Inter', sans-serif; transition: all .15s; }
.tbtn.active { border-color: var(--teal); color: var(--teal); }
.tbtn:hover:not(.active) { color: var(--text); border-color: var(--dim); }

.page { padding: 6px 24px 48px; animation: rbx-fi .18s ease; }
@keyframes rbx-fi { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: translateY(0); } }
.ptitle { font-size: 14px; font-weight: 600; color: var(--text); margin-bottom: 2px; letter-spacing: -.01em; }
.psub { font-size: 11px; color: var(--muted); margin-bottom: 14px; }

.grid { display: grid; gap: 8px; width: 100%; margin-bottom: 8px; }
.g-auto-3 { grid-template-columns: 180px repeat(3, minmax(0, 1fr)); }
.g-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.g-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.g-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.g-2-1 { grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); }
.g-1-2 { grid-template-columns: minmax(0, 1fr) minmax(0, 2fr); }

.c { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 14px; overflow: hidden; transition: background .2s, border-color .2s; }
.c-link { cursor: pointer; transition: border-color .18s; }
.c-link:hover { border-color: var(--teal); }

.ch-dial { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 5px; text-align: center; padding: 18px 14px; }
.ch-met { display: flex; flex-direction: column; }
.ch-ai {}
.ch-bar { min-height: 210px; }
.ch-heat {}
.ch-chart {}
.ch-trend { min-height: 180px; }
.ch-sim { min-height: 240px; }
.ch-tbl {}

.clbl { display: block; font-size: 10px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; color: var(--muted); margin-bottom: 6px; }
.cval { font-size: 26px; font-weight: 600; color: var(--text); line-height: 1; margin-bottom: 3px; letter-spacing: -.02em; }
.cval span { font-size: 13px; font-weight: 400; color: var(--dim); }
.csub { display: block; font-size: 11px; color: var(--muted); margin-bottom: 8px; }
.ctitle { display: block; font-size: 12px; font-weight: 600; color: var(--text); margin-bottom: 8px; }
.delta { display: inline-flex; align-items: center; gap: 3px; font-size: 11px; font-weight: 600; padding: 2px 7px; border-radius: 999px; }
.dup { background: var(--green-bg); color: var(--green-text); }
.ddn { background: var(--red-bg); color: var(--red-text); }
.kbtn { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 600; padding: 6px 12px; border-radius: 6px; border: 1px solid var(--teal); color: var(--teal); background: rgba(0,191,165,.08); transition: all .18s; cursor: pointer; font-family: 'Inter', sans-serif; text-decoration: none; }
.kbtn:hover { background: var(--teal); color: #fff; }
.ghost-btn { font-size: 11px; font-weight: 500; padding: 5px 12px; border-radius: 6px; border: 1px solid var(--border2); background: transparent; color: var(--muted); cursor: pointer; font-family: 'Inter', sans-serif; transition: all .15s; }
.ghost-btn:hover { color: var(--text); border-color: var(--dim); }

.swrap { position: relative; width: 100%; height: 52px; margin-top: 8px; margin-bottom: 10px; }

.dgrade { font-size: 11px; font-weight: 600; padding: 3px 10px; border-radius: 999px; background: var(--red-bg); color: var(--red-text); }
.ddelta { font-size: 11px; color: var(--muted); }
.dnote { font-size: 10px; color: var(--dim); margin-top: 6px; padding-top: 6px; border-top: 1px solid var(--border); width: 100%; line-height: 1.55; }
.ai-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: .5rem; }
.aib { font-size: 10px; padding: 2px 7px; border-radius: 999px; background: var(--surface2); color: var(--muted); border: 1px solid var(--border2); }
.aits { font-size: 10px; color: var(--dim); }
.aibd { font-size: 12px; color: var(--muted); line-height: 1.75; margin-bottom: .55rem; }
.aibd strong { font-weight: 600; color: var(--text); }
.aisigs { display: flex; gap: 5px; flex-wrap: wrap; margin-bottom: .55rem; }
.sig { font-size: 11px; font-weight: 500; padding: 3px 9px; border-radius: 999px; border: 1px solid var(--border2); color: var(--muted); background: var(--surface2); display: flex; align-items: center; gap: 4px; }
.sdot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
.aifoot { display: flex; justify-content: space-between; align-items: center; padding-top: .5rem; border-top: 1px solid var(--border); }
.aifn { font-size: 10px; color: var(--dim); }
.groupby-wrap { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.groupby { font-size: 11px; padding: 3px 22px 3px 8px; border-radius: 6px; border: 1px solid var(--border2); background: var(--surface2); color: var(--muted); font-family: 'Inter', sans-serif; cursor: pointer; appearance: none; background-image: url("data:image/svg+xml,%3Csvg width='7' height='4' viewBox='0 0 7 4' fill='none'%3E%3Cpath d='M1 1l2.5 2.5L6 1' stroke='%236B7591' stroke-width='1' stroke-linecap='round'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 7px center; }
.brows { display: flex; flex-direction: column; gap: 6px; }
.brow { display: flex; align-items: center; gap: 8px; cursor: pointer; }
.brow:hover .blbl { color: var(--teal); }
.blbl { font-size: 11px; color: var(--muted); width: 82px; flex-shrink: 0; transition: color .15s; }
.btrack { flex: 1; background: var(--bar-track); border-radius: 3px; height: 7px; overflow: hidden; }
.bfill { height: 7px; border-radius: 3px; }
.bval { font-size: 11px; color: var(--dim); width: 22px; text-align: right; flex-shrink: 0; font-weight: 600; }

.heatmap { display: grid; grid-template-columns: repeat(4,1fr); gap: 5px; }
.hcell { padding: 10px 8px; text-align: center; cursor: pointer; transition: opacity .15s; border-radius: 7px; position: relative; overflow: hidden; }
.hcell:hover { opacity: .85; }
.hname { font-size: 10px; font-weight: 600; letter-spacing: .03em; }
.hscore { font-size: 18px; font-weight: 700; margin-top: 2px; }
.hcell::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: rgba(255,255,255,.25); border-radius: 7px 7px 0 0; }
.rbx-root[data-theme="dark"] .hcell::before { background: rgba(255,255,255,.08); }

.funstage { margin-bottom: 9px; }
.ftop { display: flex; justify-content: space-between; margin-bottom: 3px; }
.flbl { font-size: 11px; color: var(--muted); }
.fright { font-size: 11px; font-weight: 600; color: var(--text); }
.fdrop { font-weight: 400; color: var(--dim); margin-left: 5px; }

.simrow { display: flex; align-items: center; justify-content: space-between; padding: 9px 0; border-bottom: 1px solid var(--border); cursor: pointer; }
.simrow:last-child { border-bottom: none; }
.simrow:hover .sname { color: var(--teal); }
.sname { font-size: 12px; font-weight: 500; color: var(--text); margin-bottom: 3px; transition: color .15s; }
.rpill { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 10px; font-weight: 600; }
.rh { background: var(--red-bg); color: var(--red-text); }
.rm { background: var(--amber-bg); color: var(--amber-text); }
.rl { background: var(--green-bg); color: var(--green-text); }
.flagbadge { font-size: 10px; font-weight: 600; padding: 2px 8px; border-radius: 999px; background: var(--red-bg); color: var(--red-text); }
.tpill { display: inline-block; font-size: 10px; font-weight: 500; padding: 2px 6px; border-radius: 999px; background: var(--surface2); color: var(--muted); border: 1px solid var(--border2); margin: 1px 2px 1px 0; }
.tpill.al { background: var(--red-bg); color: var(--red-text); border-color: transparent; }
.stars { display: inline-flex; gap: 2px; vertical-align: middle; }

.tbl { width: 100%; border-collapse: collapse; font-size: 12px; }
.tbl th { font-size: 10px; font-weight: 600; color: var(--dim); letter-spacing: .05em; text-transform: uppercase; padding: 0 8px 6px; border-bottom: 1px solid var(--border); text-align: left; white-space: nowrap; }
.tbl td { padding: 8px; border-bottom: 1px solid var(--border); color: var(--muted); }
.tbl tr:last-child td { border-bottom: none; }
.tbl tr:hover td { background: var(--surface2); cursor: pointer; }
.pct-hi { color: var(--red); font-weight: 600; }
.pct-md { color: var(--amber); font-weight: 600; }
.pct-lo { color: var(--green); font-weight: 600; }

.users-breakdown { display: flex; gap: 6px; margin-top: 6px; margin-bottom: 2px; }
.ub-item { flex: 1; border: 1px solid var(--border2); border-radius: 7px; padding: 6px 8px; }
.ub-n { font-size: 16px; font-weight: 600; color: var(--text); letter-spacing: -.01em; }
.ub-lbl { font-size: 10px; color: var(--muted); margin-top: 1px; }

.drawer-ov { position: fixed; inset: 0; background: rgba(0,0,0,.4); display: flex; justify-content: flex-end; z-index: 1300; }
.drawer { background: var(--surface); width: 400px; max-width: 90vw; height: 100%; border-left: 1px solid var(--border); padding: 16px; overflow-y: auto; animation: rbx-si .18s ease; }
@keyframes rbx-si { from { transform: translateX(100%); } to { transform: translateX(0); } }
.dtitle { font-size: 13px; font-weight: 600; color: var(--text); margin-bottom: 2px; padding-right: 20px; }
.dsub { font-size: 11px; color: var(--muted); margin-bottom: 12px; }
.dclose { float: right; font-size: 13px; cursor: pointer; color: var(--muted); transition: color .15s; }
.dclose:hover { color: var(--text); }
.dlbl { font-size: 10px; font-weight: 600; letter-spacing: .07em; text-transform: uppercase; color: var(--dim); margin-bottom: 6px; display: block; }
.logrow { font-size: 11px; font-family: 'Courier New', monospace; color: var(--muted); padding: 5px 0; border-bottom: 1px solid var(--border); display: flex; gap: 10px; }
.logrow:last-child { border-bottom: none; }
.logk { color: var(--text); font-weight: 600; min-width: 120px; flex-shrink: 0; }
.legrow { display: flex; gap: 10px; align-items: center; }
.legitem { display: flex; align-items: center; gap: 4px; font-size: 11px; color: var(--muted); }
.legsq { display: inline-block; width: 8px; height: 8px; border-radius: 2px; flex-shrink: 0; }

.rep-grid { display: grid; grid-template-columns: 280px 1fr; gap: 12px; align-items: start; }
.rep-type-list { display: flex; flex-direction: column; gap: 6px; }
.rep-type-btn { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 8px; border: 1px solid var(--border2); background: transparent; cursor: pointer; font-family: 'Inter', sans-serif; transition: all .15s; text-align: left; width: 100%; }
.rep-type-btn:hover { border-color: var(--teal); background: rgba(0,191,165,.05); }
.rep-type-btn.active { border-color: var(--teal); background: rgba(0,191,165,.1); }
.rep-type-icon { width: 30px; height: 30px; border-radius: 7px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 14px; }
.rep-type-name { font-size: 12px; font-weight: 600; color: var(--text); }
.rep-type-desc { font-size: 10px; color: var(--muted); margin-top: 1px; }
.rep-panel { display: flex; flex-direction: column; gap: 8px; }
.rep-section { display: flex; flex-direction: column; gap: 6px; }
.rep-label { font-size: 10px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; color: var(--dim); }
.rep-row { display: flex; gap: 8px; flex-wrap: wrap; }
.rep-select { font-size: 11px; padding: 5px 22px 5px 8px; border-radius: 6px; border: 1px solid var(--border2); background: var(--surface2); color: var(--muted); font-family: 'Inter', sans-serif; cursor: pointer; appearance: none; background-image: url("data:image/svg+xml,%3Csvg width='7' height='4' viewBox='0 0 7 4' fill='none'%3E%3Cpath d='M1 1l2.5 2.5L6 1' stroke='%236B7591' stroke-width='1' stroke-linecap='round'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 7px center; }
.rep-input { font-size: 11px; padding: 5px 10px; border-radius: 6px; border: 1px solid var(--border2); background: var(--surface2); color: var(--text); font-family: 'Inter', sans-serif; outline: none; transition: border-color .15s; }
.rep-input:focus { border-color: var(--teal); }
.rep-input::placeholder { color: var(--dim); }
.rep-preview { background: var(--surface2); border: 1px solid var(--border2); border-radius: 8px; padding: 12px; }
.rep-preview-title { font-size: 11px; font-weight: 600; color: var(--text); margin-bottom: 8px; }
.rep-preview-row { display: flex; justify-content: space-between; align-items: center; padding: 4px 0; border-bottom: 1px solid var(--border); }
.rep-preview-row:last-child { border-bottom: none; }
.rep-preview-k { font-size: 11px; color: var(--muted); }
.rep-preview-v { font-size: 11px; font-weight: 600; color: var(--text); }
.rep-actions { display: flex; gap: 8px; align-items: center; margin-top: 4px; }
.rep-action-btn { display: inline-flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 600; padding: 8px 16px; border-radius: 7px; border: none; cursor: pointer; font-family: 'Inter', sans-serif; transition: all .18s; }
.rep-dl { background: var(--teal); color: #fff; }
.rep-dl:hover { filter: brightness(0.92); }
.rep-email-wrap { display: flex; gap: 6px; align-items: center; flex: 1; }
.rep-email-input { font-size: 11px; padding: 7px 10px; border-radius: 6px; border: 1px solid var(--border2); background: var(--surface2); color: var(--text); font-family: 'Inter', sans-serif; outline: none; flex: 1; transition: border-color .15s; }
.rep-email-input:focus { border-color: var(--teal); }
.rep-email-input::placeholder { color: var(--dim); }
.rep-send-btn { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 600; padding: 7px 14px; border-radius: 6px; border: 1px solid var(--teal); color: var(--teal); background: rgba(0,191,165,.08); cursor: pointer; font-family: 'Inter', sans-serif; transition: all .18s; white-space: nowrap; }
.rep-send-btn:hover { background: var(--teal); color: #fff; }
.toast { position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); background: var(--text); color: var(--bg); font-size: 12px; font-weight: 500; padding: 9px 18px; border-radius: 8px; z-index: 1400; opacity: 0; transition: opacity .2s; pointer-events: none; }
.toast.show { opacity: 1; }
.format-tabs { display: flex; gap: 4px; }
.fmt-btn { font-size: 11px; font-weight: 500; padding: 4px 12px; border-radius: 6px; border: 1px solid var(--border2); background: transparent; color: var(--muted); cursor: pointer; font-family: 'Inter', sans-serif; transition: all .15s; }
.fmt-btn.active { background: var(--surface2); color: var(--text); border-color: var(--dim); }
.fmt-btn:hover:not(.active) { color: var(--text); }
.nav-reports-btn { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 500; padding: 3px 10px; border-radius: 999px; border: 1px solid var(--border2); background: transparent; color: var(--muted); cursor: pointer; font-family: 'Inter', sans-serif; transition: all .15s; }
.nav-reports-btn:hover, .nav-reports-btn.active { color: var(--teal); border-color: var(--teal); background: rgba(0,191,165,.07); }

@media (prefers-reduced-motion: reduce) {
  .rbx-root *, .rbx-root *::before, .rbx-root *::after {
    animation-duration: .01ms !important;
    transition-duration: .01ms !important;
  }
}


`;

function fmtTime(d) {
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function FeatureOffNotice({ feature }) {
    return (
        <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--muted)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
                {feature} is turned off for this tenancy
            </div>
            <div style={{ fontSize: 11 }}>Enable it in the Features section of the portal to access this page.</div>
        </div>
    );
}

class PageErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }
    static getDerivedStateFromError(error) {
        return { error };
    }
    componentDidCatch(error, info) {
        console.error('[reports-b] render error:', error, info);
    }
    render() {
        if (this.state.error) {
            return (
                <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--muted)' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>Something went wrong</div>
                    <div style={{ fontSize: 11, marginBottom: 16 }}>{this.state.error.message}</div>
                    <button
                        onClick={() => this.setState({ error: null })}
                        className="ghost-btn"
                    >
                        Try again
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

// Deep-link guard: the route is mounted whether or not the current tenancy has
// reports-b, so the page itself enforces the enableReportsB switch (same source
// the menu gate uses) for EVERYONE — super admins included: with the switch off
// for the toggled tenancy, a pasted /reports-b URL shows the off-notice, not the
// dashboard. To preview a tenancy, flip its switch on in Features first. A thin
// wrapper keeps the guard's conditional return away from the main component's
// hooks; the loading check avoids flashing the off-notice before the switches
// arrive on a fresh login or tenancy toggle.
export default function ReportsBPage() {
    const { featureSwitches, featureSwitchesLoading, tenancyId } = useSelector((s) => s.tenancy) || {};
    if (featureSwitchesLoading && !featureSwitches) return null;
    if (!featureSwitches?.enableReportsB) {
        return <FeatureOffNotice feature="Reports" />;
    }
    return <ReportsBPageInner key={String(tenancyId)} />;
}

function ReportsBPageInner() {
    const { themeMode } = useThemeContext();
    const baseTheme = useTheme();

    // MUI Tooltips portal to <body> (outside .rbx-root) and default to a dark
    // box in BOTH themes. Re-style them to the portal's surface tokens so chart
    // and icon hover-overs adhere to light/dark like the Recharts tooltips do.
    const rbxTheme = useMemo(() => createTheme(baseTheme, {
        components: {
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        backgroundColor: baseTheme.palette.background.paper,
                        color: baseTheme.palette.text.primary,
                        border: `1px solid ${baseTheme.palette.divider}`,
                        boxShadow: baseTheme.shadows[3],
                        fontSize: 11,
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500
                    },
                    arrow: {
                        color: baseTheme.palette.background.paper,
                        "&::before": {
                            border: `1px solid ${baseTheme.palette.divider}`,
                            backgroundColor: baseTheme.palette.background.paper
                        }
                    }
                }
            }
        }
    }), [baseTheme]);

    const viewedTenancyId = useSelector((s) => s.tenancy.tenancyId);
    const loggedInUser = useMemo(() => {
        try { return JSON.parse(localStorage.getItem('user')) || {}; } catch { return {}; }
    }, []);
    const [restored] = useState(() => {
        const state = restoreShellState(loggedInUser.id, String(viewedTenancyId));
        setExcludeDisabled(state.excludeDisabled);
        return state;
    });
    const excludeDisabled = useExcludeDisabled();

    const [period, setPeriodRaw] = useState(restored.period);
    // Single search term shared across pages so it persists during navigation.
    const [search, setSearch] = useState('');
    // Seeded from the same resolver the chips use, so the initial window and
    // the 6m chip cannot drift apart.
    const [startDate, setStartDate] = useState(restored.startDate);
    const [endDate, setEndDate] = useState(restored.endDate);
    const [navLocked, setNavLocked] = useState(() => {
        try { return localStorage.getItem('rbx-nav-locked') === '1'; } catch { return false; }
    });
    const toggleNavLock = useCallback(() => {
        setNavLocked((v) => {
            const next = !v;
            try { localStorage.setItem('rbx-nav-locked', next ? '1' : '0'); } catch { /* ignore */ }
            return next;
        });
    }, []);

    // Period chips and the date pickers are bound to the same underlying
    // window. Picking a chip resolves it to a start/end and writes both;
    // editing a date clears the chip selection (handled inside FilterBar).
    // PERIOD_TO_RANGE is shared with the query layer so both agree on what a
    // token means; an unknown token leaves the dates alone.
    const setPeriod = useCallback((p) => {
        setPeriodRaw(p);
        const fn = PERIOD_TO_RANGE[p];
        if (fn) { const r = fn(); setStartDate(r.start); setEndDate(r.end); }
    }, []);
    const [nav, setNavRaw] = useState(() => ({ stack: buildStack(restored.tab, restored.filters), history: [], sharedFilters: restored.filters }));
    const setNav = useCallback((update) => {
        setNavRaw((n) => withSharedFilters(typeof update === 'function' ? update(n) : update, n.sharedFilters));
    }, []);
    const stack = nav.stack;
    const savedFilters = nav.sharedFilters;
    const setStack = useCallback((update) => {
        setNav((n) => ({ ...n, stack: typeof update === 'function' ? update(n.stack) : update }));
    }, [setNav]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerItem, setDrawerItem] = useState(null);
    const [drawerType, setDrawerType] = useState(null);
    const [lastRefreshed, setLastRefreshed] = useState(new Date());
    const [spinning, setSpinning] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    // Page remounts are keyed off dataVersion (bumped when a refetch settles),
    // so a manual refresh replays the entrance animation exactly once.
    // In-product CUSTOMISE drawer + FEATURES dialog were removed in favour
    // of the portal Features page. The corresponding state variables are
    // gone with them; widget visibility and feature flags still resolve
    // via the shared localStorage keys read by useWidgetVisibility /
    // featureFlags.js.
    const [paletteOpen, setPaletteOpen] = useState(false);
    const [customiseOpen, setCustomiseOpen] = useState(false);

    // ---- Live data hydration ------------------------------------------------
    // Four dashboard-endpoint feeds + the simulation catalogue hydrate the
    // ./data module (same canonical shapes the prototype generated), then the
    // risk model recomputes over them. Pages stay unmounted until the first
    // hydration so every card renders against real rows; dataVersion remounts
    // the page tree after each refresh.
    const [dataReady, setDataReady] = useState(false);
    const [dataVersion, setDataVersion] = useState(0);
    // 13 months of raw events covers the 12m period chips plus the risk
    // model's 180-day window recomputed at month-ends up to 6 months back.
    const dataRanges = useMemo(() => {
        const iso = (d) => d.toISOString().slice(0, 10);
        const now = new Date();
        const back = (months) => { const d = new Date(now.getTime()); d.setUTCMonth(d.getUTCMonth() - months); return d; };
        return {
            events: { startDate: browserEventsStart(now), endDate: iso(now) },
            bench: { startDate: iso(back(12)), endDate: iso(now) },
        };
    }, []);
    // Derive the accumulated scope from the full breadcrumb stack. Values for
    // each type collect into arrays so users can stack multiple filters of
    // the same category (e.g. two departments) and see combined data.
    // 'metaType' is a navigation pivot indicator (which dimension to list),
    // not a data filter, skip it so downstream pages don't see a phantom
    // scope key and trigger isScoped logic spuriously.
    const scope = stack.reduce((acc, entry) => {
        if (entry.context && entry.context.type && entry.context.type !== 'metaType' && entry.context.type !== 'performance') {
            const { type, value } = entry.context;
            if (!acc[type]) acc[type] = [];
            if (!acc[type].includes(value)) acc[type].push(value);
        }
        return acc;
    }, {});

    // The client ./data layer is hydrated for the VIEWED tenancy ONLY, never the
    // whole subtree. Pooling a large subtree (e.g. 130k users) into the client
    // froze the risk widgets and blew past the 20k roster-hydration cap, which
    // dropped small child-tenancy departments before they reached the metadata
    // dropdowns. The server-backed widgets (Risk score, Sims/Courses/Users
    // tables, league) still pool subtree-wide via their own serverScope.tenancy,
    // so the aggregate numbers stay subtree-wide; only the client-computed layer
    // is bounded here. Memoised on the tenancy id so the six hydration queries
    // below stay referentially stable and don't thrash.
    const tenancyName = useSelector((s) => s.tenancy.tenancyName);
    const dashboardBusy = useSelector(selectDashboardBusy);

    // ---- Scoped (locked) non-admin experience (BD-901) ----------------------
    // A regular user (role === '') with a reportingLevel assigned can reach this
    // page, but only to see their own slice. The banner tells them what they're
    // locked to and the widening controls (tenancy switcher + dimension pickers)
    // are removed below. The server is the real enforcer of the data scope; the
    // UI just refuses to pretend the caller can pick another department/tenancy.
    const isRegularUser = (loggedInUser.role || '') === '';
    // Full record carries the authoritative reportingLevel + the caller's own
    // department/location/... value for the banner; the localStorage copy can be
    // stale. Only fetched for regular users so the admin path is untouched.
    const { data: fullUserData } = useGetUserByIdQuery(
        { userId: loggedInUser.id },
        { skip: !isRegularUser || !loggedInUser.id }
    );
    const reportingLevel = fullUserData?.reportingLevel ?? loggedInUser.reportingLevel ?? null;
    const isScopedUser = isScopedReportingUser({ role: loggedInUser.role, reportingLevel });
    const scopeBanner = useMemo(
        () => (isScopedUser ? buildScopeBanner({ reportingLevel, user: fullUserData, tenancyName }) : ''),
        [isScopedUser, reportingLevel, fullUserData, tenancyName]
    );

    // Client ./data hydration scope. Admins/tenancy admins pool the VIEWED
    // tenancy (see note below). Scoped users send NO scope so the server derives
    // and clamps to their identity slice — the client never tries to broaden.
    const selfTenancyScope = useMemo(
        () => (isScopedUser ? {} : { tenancy: [Number(viewedTenancyId)] }),
        [viewedTenancyId, isScopedUser]
    );
    // Scope with the tenancy dimension removed, for the drill/detail pages (DRILL_PAGES):
    // they must not receive a persisted child-tenancy filter (it errors / doesn't belong on a
    // single-item view). Other dimensions still apply. Memoised so it stays referentially stable.
    const drillScope = useMemo(() => {
        if (!scope.tenancy) return scope;
        const { tenancy, ...rest } = scope;
        return rest;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(scope)]);
    const simsQ = useDashboardData({ metric: 'simulations', aggregation: 'events', ...dataRanges.events, scope: selfTenancyScope });
    const coursesQ = useDashboardData({ metric: 'courses', aggregation: 'events', ...dataRanges.events, scope: selfTenancyScope });
    const remindersQ = useDashboardData({ metric: 'reminders', aggregation: 'events', ...dataRanges.events, scope: selfTenancyScope });
    const rosterQ = useDashboardData({ metric: 'users', aggregation: 'roster', ...dataRanges.events, scope: selfTenancyScope });
    const benchQ = useDashboardData({ metric: 'summary', aggregation: 'benchmark', ...dataRanges.bench, scope: selfTenancyScope });
    const simTagsQ = useDashboardData({ metric: 'simulations', aggregation: 'sim_tags', scope: selfTenancyScope });
    const courseTagsQ = useDashboardData({ metric: 'courses', aggregation: 'course_tags', scope: selfTenancyScope });
    const catalogQ = useGetSimulationsQuery({ perPage: 1000, page: 0, includes: '', query: '', additionalQueryParams: '' });
    // Metadata dropdown OPTIONS are sourced from a lightweight server aggregation
    // over the WHOLE subtree (NO scope -> the BFF resolves self+descendants), not
    // the bounded client ./data above. ~300 rows, ~1s, cached. This is what lets a
    // small child-tenancy department/location/... still appear in the filter
    // dropdowns even though the client roster is bounded to the viewed tenancy.
    // Referentially stable: dimValuesQ.data only changes when the aggregation does.
    const dimValuesQ = useDashboardData({ metric: 'users', aggregation: 'dim_values' });
    const dimOptions = useMemo(() => buildDimOptions(dimValuesQ.data?.dimValues), [dimValuesQ.data]);
    const allSettled = !simsQ.isFetching && !coursesQ.isFetching && !remindersQ.isFetching && !rosterQ.isFetching && !benchQ.isFetching && !catalogQ.isFetching && !courseTagsQ.isFetching && !simTagsQ.isFetching;

    useEffect(() => {
        if (!allSettled) return;
        // Authored course tags must land before hydrateData: COURSES/COURSES_BY_TYPE bake
        // courseType() in during hydration, so a late seed would leave keyword-classified types.
        setAuthoredCourseTags(courseTagsQ.data?.courseTags || []);
        if (Array.isArray(simTagsQ.data?.simTags)) setServerSimTags(simTagsQ.data.simTags);
        hydrateData({
            users: rosterQ.data?.records || [],
            simEvents: simsQ.data?.records || [],
            courseEvents: coursesQ.data?.records || [],
            reminderEvents: remindersQ.data?.records || [],
            simCatalog: Array.isArray(catalogQ.data) ? catalogQ.data : [],
        });
        recomputeRiskModel(benchQ.data?.benchmark || null);
        applyRiskScores(USER_SCORES);
        setDataVersion((v) => v + 1);
        setDataReady(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allSettled, simsQ.data, coursesQ.data, remindersQ.data, rosterQ.data, benchQ.data, catalogQ.data, courseTagsQ.data, simTagsQ.data]);

    // CmdK / Ctrl+K opens the palette anywhere on the dashboard. Bound at
    // window level so input fields don't have to opt in.
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
                e.preventDefault();
                setPaletteOpen((p) => !p);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);
    // Re-render the shell when feature flags change so NavBar entries
    // appear/disappear immediately on toggle.
    const [, forceFeatureRender] = useState(0);
    useEffect(() => subscribeFeatures(() => forceFeatureRender((n) => n + 1)), []);
    const summaryVisibility = useWidgetVisibility('summary');
    const simsVisibility = useWidgetVisibility('sims');
    const coursesVisibility = useWidgetVisibility('courses');
    // The three hooks share one deduped layout query; wait for it to be applied
    // before rendering pages so the saved/inherited layout shows without a flash
    // of the default. Ready immediately when there's no tenancy or it errored.
    const layoutReady = summaryVisibility.ready;

    const handleManualRefresh = useCallback(() => {
        if (refreshing) return;
        setSpinning(true);
        setRefreshing(true);
        // Real refetches; the hydration effect rebuilds the data layer and
        // bumps dataVersion (remounting pages) when they settle. The timer
        // below only drives the existing progress-bar/spin visuals.
        simsQ.refetch();
        coursesQ.refetch();
        rosterQ.refetch();
        benchQ.refetch();
        catalogQ.refetch();
        setTimeout(() => {
            setLastRefreshed(new Date());
            setSpinning(false);
            setRefreshing(false);
        }, 900);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshing]);

    useEffect(() => {
        const interval = setInterval(() => setLastRefreshed(new Date()), 60 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    const currentEntry = stack[stack.length - 1];
    const currentPage = currentEntry.id;
    const currentData = currentEntry.context?.value ?? null;
    const performanceEntry = currentPage === PERFORMANCE_PAGE
        ? [...stack].reverse().find((e) => e.id === PERFORMANCE_PAGE && e.context?.type === 'performance')
        : null;
    const performance = performanceEntry ? parsePerformanceContext(performanceEntry.context.value) : null;
    const metaList = currentPage === 'meta-list' ? metaListContext(stack) : null;
    // A page's own drill context (Users sort, send-log status) is on its first crumb, and
    // filters applied on the page add crumbs after it, so look back through the page's run.
    const pageContextValue = (type) => {
        for (let i = stack.length - 1; i > 0 && stack[i].id === currentPage; i -= 1) {
            if (stack[i].context?.type === type) return stack[i].context.value;
        }
        return undefined;
    };

    // Chart libs (Recharts/Apex) measure on mount; trigger a resize after a
    // page swap so they recompute against the new layout. Previously this was
    // fired by motion.div's onAnimationComplete; now that the page-level fade
    // only runs once, dispatch the event ourselves on currentPage change.
    useEffect(() => {
        const id = requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
        return () => cancelAnimationFrame(id);
    }, [currentPage]);

    // No window snap on users / user-detail. Both are now date-filterable like
    // Sims/Courses, so they inherit the current shared window instead of being
    // forced to 12m. In particular a drill into user-detail keeps whatever range
    // was active on the origin page (the FilterBar pre-populates from it).

    // Scroll the document back to the top whenever the visible page
    // changes. Anchor pages and detail drill-ins were previously inheriting
    // whatever scroll offset the user left behind on the origin page, so a
    // "View all" button looked like it didn't change anything until the
    // user manually scrolled. Doing this in one place (here, where every
    // navigation flows through) is cheaper than asking each click handler
    // to remember it.
    const scrollToTop = () => {
        // Window-level scroll for the document body, plus the dashboard's
        // own scroll container if present, different layouts wrap content
        // in a scrollable div instead of relying on the viewport.
        if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'auto' });
        if (typeof document !== 'undefined') {
            const root = document.querySelector('.rbx-root');
            if (root) root.scrollTop = 0;
        }
    };

    const go = useCallback((id, label, context = null) => {
        setNav(({ stack: prev, history, sharedFilters }) => {
            const currentId = prev[prev.length - 1].id;
            const nextHistory = currentId === id ? history : [...history, prev];
            // Carry any active scope-dimension crumbs across the tab switch,
            // re-homed onto the destination page id so Breadcrumb (which
            // collapses by id) folds them under the destination's crumb and
            // currentPage still resolves to it. The destination entry itself is
            // kept LAST so its own context stays authoritative. With no active
            // scope, `carried` is empty and this matches the previous behaviour
            // exactly (summary -> [summary], other -> [summary, page]).
            // A drill into user-detail pins its user as that crumb's context, but that is
            // the drilled-into item, NOT a filter the user chose - so it must not be carried
            // onto sibling pages when navigating away. Only the filters active BEFORE the
            // drill should survive; those live on normal page crumbs. A user FILTER applied
            // via the User dropdown lives on a page crumb (id !== 'user-detail') and still carries.
            const carried = SHARED_AREA_IDS.has(id) && !isSharedArea(prev) ? sharedFilters : sharedFiltersOf(prev);
            return {
                stack: buildStack(id === 'summary' ? null : { id, label }, carried, context),
                history: nextHistory,
            };
        });
        setDrawerOpen(false);
        setDrawerItem(null);
        scrollToTop();
    }, [setNav]);

    const drill = useCallback((id, label, context = null) => {
        setNav(({ stack: prev, history }) => {
            const currentId = prev[prev.length - 1].id;
            if (id === currentId && !DRILL_PAGES.has(currentId) && context && FILTER_TYPES.includes(context.type)) {
                return { stack: [...prev, { id, label, context, filter: true }], history };
            }
            const nextHistory = [...history, prev];
            if (currentId.endsWith('-detail')) {
                // Find start of the consecutive detail-page run at the tail
                let branchStart = prev.length - 1;
                while (branchStart > 0 && prev[branchStart - 1].id.endsWith('-detail')) {
                    branchStart--;
                }
                // If already 2+ detail pages deep, replace the whole branch
                if (prev.length - branchStart >= 2) {
                    return { stack: [...prev.slice(0, branchStart), { id, label, context }], history: nextHistory };
                }
            }
            // Drilling out of meta-list (either pivoting to another type or selecting a row)
            // replaces its crumbs so they don't bloat the trail. Filters applied while on
            // it are re-homed onto the destination rather than dropped with them.
            if (currentId === 'meta-list') {
                const start = metaListRunStart(prev);
                const kept = prev.slice(0, start);
                const target = { id, label, context };
                return { stack: [...kept, ...carryFilters(prev.slice(start), kept, target), target], history: nextHistory };
            }
            return { stack: [...prev, { id, label, context }], history: nextHistory };
        });
        setDrawerOpen(false);
        setDrawerItem(null);
        scrollToTop();
    }, [setNav]);

    // removeScope(type)        -> drop every entry of this filter type
    // removeScope(type, value)  -> drop just the one matching entry, so users
    //                             can peel filters off a stacked set one at a time
    // Options for the page-level filter chips (Simulation / Course / User) that
    // render inline with the dimension chips. Built once per hydration.
    const simChipOptions = useMemo(
        () => SIMS.map((x) => ({ label: x.name, value: x.name })).sort((a, b) => a.label.localeCompare(b.label)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [DATA_EPOCH],
    );
    const courseChipOptions = useMemo(
        () => COURSES.map((c) => ({ label: c.name, value: c.name })).sort((a, b) => a.label.localeCompare(b.label)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [DATA_EPOCH],
    );
    const userChipOptions = useMemo(
        () => USERS.map((u) => ({ label: u.email ? `${u.name} (${u.email})` : u.name, value: u.email || u.name, display: u.name })).sort((a, b) => a.label.localeCompare(b.label)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [DATA_EPOCH],
    );
    // Tenancy scope options (BD-565/629): the active tenancy's descendant
    // tenancies, derived client-side from the tenancy list. Values are numeric
    // ids and stay Numbers through drill/scope so the backend receives
    // scope.tenancy as number[]. Empty => the tenancy has no children, so the
    // chip is not rendered anywhere.
    const { options: tenancyChipOptions, loaded: tenancyOptionsLoaded } = useDescendantTenancyState();

    const selfTenancyId = Number(viewedTenancyId);
    const selfTenancyLabel = tenancyChipOptions.find((o) => o.value === selfTenancyId)?.label || String(selfTenancyId);
    const selfTenancyFilters = useMemo(
        () => (isScopedUser ? [] : defaultTenancyIds(tenancyChipOptions, selfTenancyId).map((value) => ({ type: 'tenancy', value }))),
        [isScopedUser, tenancyChipOptions, selfTenancyId]
    );
    const defaultTenancyKey = useMemo(
        () => selfTenancyFilters.map((c) => Number(c.value)).sort((a, b) => a - b).join(','),
        [selfTenancyFilters]
    );
    const seededTenancyRef = useRef(restored.hasSaved ? selfTenancyId : null);
    const scopedUserPending = isRegularUser && !!loggedInUser.id && !fullUserData;
    useEffect(() => {
        if (scopedUserPending || !selfTenancyFilters.length || seededTenancyRef.current === selfTenancyId) return;
        seededTenancyRef.current = selfTenancyId;
        setNav((n) => {
            if (n.stack.some((entry) => entry.context?.type === 'tenancy')) return n;
            const anchor = { id: n.stack[n.stack.length - 1].id, label: selfTenancyLabel };
            return {
                ...n,
                stack: [...n.stack, ...toFilterEntries(selfTenancyFilters, anchor)],
                sharedFilters: n.sharedFilters.some((c) => c.type === 'tenancy') ? n.sharedFilters : [...n.sharedFilters, ...selfTenancyFilters],
            };
        });
    }, [scopedUserPending, selfTenancyFilters, selfTenancyId, selfTenancyLabel, setNav]);
    // Until that default is in the stack the pages aren't mounted, so they query once
    // with it rather than once without it and again with it (BD-1112). Only a visit
    // with nothing saved waits: a restored session already carries its tenancy filter.
    const tenancySeedPending = defaultTenancyPending({
        seededFor: seededTenancyRef.current,
        tenancyId: selfTenancyId,
        scopedUserPending,
        isScopedUser,
        optionsLoaded: tenancyOptionsLoaded,
        defaultFilters: selfTenancyFilters,
    });
    const pageReady = layoutReady && !tenancySeedPending;

    const persistedFilters = JSON.stringify(serialiseReportFilters({
        tab: sharedTabOf(stack),
        filters: savedFilters,
        period,
        startDate,
        endDate,
        excludeDisabled,
    }));
    useEffect(() => {
        writeReportFilterSession(loggedInUser.id, viewedTenancyId, persistedFilters);
    }, [loggedInUser.id, viewedTenancyId, persistedFilters]);

    const resetFilters = useCallback(() => {
        setPeriod(DEFAULT_PERIOD);
        setExcludeDisabled(false);
        setNav((n) => {
            const kept = n.stack.filter((e, i) => i === 0 || !(e.filter && e.context && FILTER_TYPES.includes(e.context.type)));
            const anchor = { id: (kept[1] || kept[0]).id, label: selfTenancyLabel };
            return { ...n, stack: [kept[0], ...toFilterEntries(selfTenancyFilters, anchor), ...kept.slice(1)], sharedFilters: selfTenancyFilters };
        });
        setDrawerOpen(false);
        setDrawerItem(null);
    }, [setPeriod, setNav, selfTenancyFilters, selfTenancyLabel]);

    const filtersAreDefault = period === DEFAULT_PERIOD && !excludeDisabled
        && savedFilters.every((c) => c.type === 'tenancy')
        && (!savedFilters.length || savedFilters.map((c) => Number(c.value)).sort((a, b) => a - b).join(',') === defaultTenancyKey);
    const allTenancySelected = useMemo(
        () => selectsEveryOption(tenancyChipOptions, Array.isArray(scope.tenancy) ? scope.tenancy : (scope.tenancy != null ? [scope.tenancy] : [])),
        [scope.tenancy, tenancyChipOptions]
    );
    const reportFiltersValue = useMemo(
        () => ({ resetFilters, isDefault: filtersAreDefault, allTenancySelected }),
        [resetFilters, filtersAreDefault, allTenancySelected]
    );

    const removeScope = useCallback((type, value) => {
        setStack((prev) => prev.filter((entry, i) => {
            if (i === 0) return true;
            const ctx = entry.context;
            if (!ctx || ctx.type !== type) return true;
            if (value !== undefined && ctx.value !== value) return true;
            return false;
        }));
        setDrawerOpen(false);
        setDrawerItem(null);
    }, [setStack]);

    // When the user types into the search bar, drop every active metadata
    // filter so search and scope don't fight each other.
    const clearScopeFilters = useCallback(() => {
        setStack((prev) => prev.filter((entry, i) => i === 0 || !entry.context || !['department','location','city','country','company','state','course','user','tenancy'].includes(entry.context.type)));
    }, [setStack]);

    const gotoCrumb = useCallback((i) => {
        setNav(({ stack: prev, history }) => {
            if (i >= prev.length - 1) return { stack: prev, history };
            const kept = prev.slice(0, i + 1);
            const target = kept[kept.length - 1];
            const carried = carryFilters(prev.slice(i + 1), kept, target);
            const stack = i === 0 ? [kept[0], ...carried] : [...kept.slice(0, -1), ...carried, target];
            return { stack, history: [...history, prev] };
        });
        setDrawerOpen(false);
        setDrawerItem(null);
    }, [setNav]);

    const goBack = useCallback(() => {
        setNav(({ stack: prev, history, sharedFilters }) => {
            if (!history.length) return { stack: prev, history };
            const snapshot = history[history.length - 1];
            const base = snapshot.filter((e) => !(e.filter && SCOPE_DIM_TYPES.includes(e.context?.type)));
            const target = base[base.length - 1];
            const from = isSharedArea(base) && !isSharedArea(prev) ? toFilterEntries(sharedFilters, RISK_ROOT) : prev;
            const carried = carryFilters(from, base, target);
            const stack = base.length === 1 ? [base[0], ...carried] : [...base.slice(0, -1), ...carried, target];
            return { stack, history: history.slice(0, -1) };
        });
        setDrawerOpen(false);
        setDrawerItem(null);
        scrollToTop();
    }, [setNav]);

    const openDrawer = useCallback((index, type) => {
        const items = type === 'sim' ? SIMS : USERS;
        setDrawerItem(items[index]);
        setDrawerType(type);
        setDrawerOpen(true);
    }, []);

    const closeDrawer = useCallback(() => {
        setDrawerOpen(false);
        setDrawerItem(null);
        setDrawerType(null);
    }, []);

    // Tenancy scope chip (BD-565/629). Behaves like the Simulation / Course /
    // User chips (custom chip, not a name-based dimension) but its values are
    // numeric tenancy ids. Rendered inline with the dimension chips on the meta
    // pages (via extraChips) and standalone on every other page, so tenancy
    // scoping is reachable everywhere the current tenancy has children. Null
    // when there are no descendants, so nothing renders for a leaf tenancy.
    // Scoped users never get the tenancy switcher/subtree affordance: they cannot
    // widen to a sibling/child tenancy, so the control must not pretend they can.
    const tenancyChip = (!isScopedUser && tenancyChipOptions.length > 0) ? (
        <FilterChip
            key="tenancy"
            label="Tenancy"
            allLabel="All"
            searchPlaceholder="Search tenancies"
            options={tenancyChipOptions}
            activeValues={Array.isArray(scope.tenancy) ? scope.tenancy : (scope.tenancy != null ? [scope.tenancy] : [])}
            onApply={({ added, removed }) => {
                removed.forEach((v) => removeScope('tenancy', v));
                added.forEach((v) => drill(currentPage, tenancyChipOptions.find((o) => o.value === v)?.label || String(v), { type: 'tenancy', value: v }));
            }}
        />
    ) : null;

    return (
        <ReportFiltersContext.Provider value={reportFiltersValue}>
        <ThemeProvider theme={rbxTheme}>
            <style dangerouslySetInnerHTML={{ __html: CSS }} />
            <div className="rbx-root" data-theme={themeMode}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {nav.history.length > 0 && (
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={goBack}
                                startIcon={<ArrowBackIcon sx={{ fontSize: '16px !important' }} />}
                                aria-label="Go back"
                                sx={{
                                    borderRadius: '8px',
                                    borderColor: 'divider',
                                    color: 'text.primary',
                                    backgroundColor: 'background.paper',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.06em',
                                    py: '4px',
                                    px: 1.25,
                                    mr: 0.5,
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                    transition: 'all 150ms',
                                    '&:hover': {
                                        borderColor: 'secondary.main',
                                        backgroundColor: 'secondary.main',
                                        color: 'common.white',
                                        boxShadow: '0 2px 6px rgba(0,191,165,0.25)',
                                    },
                                    '& .MuiButton-startIcon': { mr: 0.5, transition: 'transform 150ms ease' },
                                    '&:hover .MuiButton-startIcon': { transform: 'translateX(-2px)' },
                                }}
                            >
                                Back
                            </Button>
                        )}
                        <Breadcrumb stack={stack} gotoStack={gotoCrumb} hideRoot={isSharedArea(stack)} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Last-refreshed indicator + manual refresh only show on
                            pages whose content is a snapshot of live data. Static
                            explainers and the builders (export, exec summary) draw
                            their content on render, refreshing them is a no-op,
                            so we hide the affordance. */}
                        {(() => {
                            const STATIC_PAGES = new Set(['risk-methodology', 'export-builder', 'export-scheduled', 'export-tables', 'export-history']);
                            if (STATIC_PAGES.has(currentPage)) return null;
                            return (
                                <>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'var(--green)', flexShrink: 0 }} />
                                    <Box component="span" sx={{ fontSize: 10, color: 'var(--dim)' }}>
                                        Last refreshed {fmtTime(lastRefreshed)} - hourly
                                    </Box>
                                    <Tooltip title="Refresh now">
                                        <IconButton
                                            size="small"
                                            onClick={handleManualRefresh}
                                            sx={{
                                                p: '3px',
                                                color: 'text.disabled',
                                                '&:hover': { color: 'text.secondary' },
                                                '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(-360deg)' } },
                                                ...(spinning && { animation: 'spin 0.8s linear' }),
                                            }}
                                        >
                                            <ReplayIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            );
                        })()}
                        {(() => {
                            const CUSTOMISABLE = new Set(['summary', 'sims', 'courses']);
                            if (!CUSTOMISABLE.has(currentPage)) return null;
                            return (
                                <Tooltip title="Customise layout">
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => setCustomiseOpen((o) => !o)}
                                        startIcon={<TuneIcon sx={{ fontSize: '14px !important' }} />}
                                        sx={{
                                            borderRadius: '6px',
                                            borderColor: 'divider',
                                            color: 'text.secondary',
                                            backgroundColor: 'background.paper',
                                            fontSize: 11,
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.04em',
                                            py: '3px',
                                            px: 1.25,
                                            '&:hover': {
                                                borderColor: 'secondary.main',
                                                color: 'secondary.main',
                                                backgroundColor: 'background.paper',
                                            },
                                            '& .MuiButton-startIcon': { mr: 0.5 },
                                        }}
                                    >
                                        Customise
                                    </Button>
                                </Tooltip>
                            );
                        })()}
                    </Box>
                </Box>

                {isScopedUser && scopeBanner && (
                    <div
                        role="status"
                        style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            margin: '0 20px 10px', padding: '8px 12px',
                            borderRadius: 8, background: 'var(--surface2)',
                            border: '1px solid var(--border2)', color: 'var(--muted)',
                            fontSize: 12,
                        }}
                    >
                        <LockOutlinedIcon sx={{ fontSize: 15, color: 'var(--teal)', flexShrink: 0 }} />
                        <span style={{ color: 'var(--text)', fontWeight: 600 }}>{scopeBanner}</span>
                        <span style={{ color: 'var(--dim)', fontSize: 11, marginLeft: 'auto' }}>
                            Read-only - limited to your data
                        </span>
                    </div>
                )}

                {(() => {
                    // FilterBar (dates only): which pages show it is decided in filterPages.js.
                    const showFilter = DATE_BEARING_PAGES.has(currentPage);
                    // MetaNav (Slice by chips) lives in the sticky lock-box too so
                    // it pins to the top alongside nav + filters. Exclude lists are
                    // page-specific: sims hide Course, meta-list inherits from its
                    // origin page.
                    const META_PAGE_EXCLUDE = {
                        summary:           ['course'],
                        sims:              ['course'],
                        'send-history':    ['course'],
                    };
                    // Every page that applies the shared scope shows the chip row
                    // (SCOPED_PAGES): without it, a drill-down that still applied the
                    // filters looked like it had lost them (BD-1112).
                    // Slice-level users don't get the "Slice by" dimension/user pickers:
                    // they imply widening to another department/user, which the UI must
                    // not offer. Tenancy-level users keep them (BD-1206), since they only
                    // narrow within the tenancy the server pins them to; the tenancy chip
                    // stays hidden for every scoped user (tenancyChip below).
                    const showMetaNav = SCOPED_PAGES.has(currentPage) && canSliceWithinTenancy({ role: loggedInUser.role, reportingLevel });
                    const showDisabledChip = !NO_DISABLED_CHIP_PAGES.has(currentPage);
                    let metaExclude = [];
                    let metaActiveType = null;
                    if (currentPage === 'sims' || currentPage === 'users' || currentPage === PERFORMANCE_PAGE) metaExclude = ['course'];
                    else if (metaList) {
                        metaActiveType = metaList.pivot?.context.value || null;
                        metaExclude = META_PAGE_EXCLUDE[metaList.origin?.id] || [];
                    }
                    return (
                        <div
                            style={{
                                position: navLocked ? 'sticky' : 'relative',
                                top: navLocked ? 8 : 0,
                                zIndex: 50,
                                margin: navLocked ? '0 12px' : 0,
                                background: navLocked ? 'var(--surface)' : 'transparent',
                                border: navLocked ? '1px solid var(--border)' : 'none',
                                borderRadius: navLocked ? 12 : 0,
                                boxShadow: navLocked ? '0 1px 2px rgba(0,0,0,0.04)' : 'none',
                            }}
                        >
                            <NavBar
                                currentPage={performance ? performance.kind : currentPage}
                                go={go}
                                openCommandPalette={() => setPaletteOpen(true)}
                                locked={navLocked}
                                onToggleLock={toggleNavLock}
                                isScopedUser={isScopedUser}
                            />
                            {showFilter && (
                                <FilterBar
                                    period={period} setPeriod={setPeriod}
                                    startDate={startDate} setStartDate={setStartDate}
                                    endDate={endDate} setEndDate={setEndDate}
                                    go={go}
                                    disabled={dashboardBusy}
                                />
                            )}
                            {showMetaNav && (
                                <div style={{ padding: '0 20px 12px' }}>
                                    <MetaNav
                                        drill={drill} removeScope={removeScope} scope={scope}
                                        activeType={metaActiveType} exclude={metaExclude} noPadX
                                        dimOptions={dimOptions}
                                        currentPage={currentPage} currentLabel={currentEntry.label}
                                        extraChips={(() => {
                                            const arr = (v) => (v == null ? [] : Array.isArray(v) ? v : [v]);
                                            const nameOf = (v) => userChipOptions.find((o) => o.value === v)?.display || v;
                                            const chips = [];
                                            if (SIM_CHIP_PAGES.has(currentPage)) {
                                                chips.push(<FilterChip key="sim" label="Simulation" searchPlaceholder="Search simulations" options={simChipOptions} activeValues={arr(scope.sim)}
                                                    onApply={({ added, removed }) => { removed.forEach((v) => removeScope('sim', v)); added.forEach((v) => drill(currentPage, v, { type: 'sim', value: v })); }} />);
                                            }
                                            if (COURSE_CHIP_PAGES.has(currentPage)) {
                                                chips.push(<FilterChip key="course" label="Course" searchPlaceholder="Search courses" options={courseChipOptions} activeValues={arr(scope.course)}
                                                    onApply={({ added, removed }) => { removed.forEach((v) => removeScope('course', v)); added.forEach((v) => drill(currentPage, v, { type: 'course', value: v })); }} />);
                                            }
                                            chips.push(<UserFilterChip key="user" options={userChipOptions} activeValues={arr(scope.user)} scope={selfTenancyScope}
                                                onApply={({ added, removed, displayOf }) => { removed.forEach((v) => removeScope('user', v)); added.forEach((v) => drill(currentPage, displayOf(v) || nameOf(v), { type: 'user', value: v })); }} />);
                                            if (tenancyChip) chips.push(tenancyChip);
                                            chips.push(<DisabledFilterChip key="disabled" />);
                                            return chips;
                                        })()}
                                    />
                                </div>
                            )}
                            {/* Tenancy scope reaches every page: on the meta pages it
                                rides in MetaNav's chip row above; everywhere else it
                                gets its own row so a child-tenancy filter is always
                                available. */}
                            {!showMetaNav && !DRILL_PAGES.has(currentPage) && (tenancyChip || showDisabledChip) && (
                                <div style={{ padding: '0 20px 12px', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                    {tenancyChip}
                                    {showDisabledChip && <DisabledFilterChip />}
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* Outside the page's keyed, animated wrapper so the pills don't
                    remount on a tab switch, and outside the data gate so restored
                    filters show while the page is still loading (BD-1112). */}
                {!tenancySeedPending && ACTIVE_FILTER_PAGES.has(currentPage) && <ActiveFilters scope={scope} removeScope={removeScope} />}

                <HydratingContext.Provider value={!dataReady}>
                {(!pageReady || (!dataReady && WAITS_FOR_DATA.has(currentPage))) && (
                    <Box sx={{ px: 3, pt: 2 }}><WidgetState loading><Box sx={{ height: 480 }} /></WidgetState></Box>
                )}
                {pageReady && (
                <PageErrorBoundary key={`${currentPage}-${dataVersion}`}>
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    style={{ paddingTop: 16 }}
                >

                        {currentPage === 'summary' && (
                            <IgnoreExcludeDisabled.Provider value>
                                <SummaryPage
                                    go={go} drill={drill} scope={scope}
                                    period={period} setPeriod={setPeriod}
                                    startDate={startDate} setStartDate={setStartDate}
                                    endDate={endDate} setEndDate={setEndDate}
                                    vis={summaryVisibility.vis}
                                    dismiss={summaryVisibility.dismiss}
                                    toggle={summaryVisibility.toggle}
                                    restoreAll={summaryVisibility.restoreAll}
                                    allWidgets={summaryVisibility.allWidgets}
                                    hidden={summaryVisibility.hidden}
                                />
                            </IgnoreExcludeDisabled.Provider>
                        )}
                        {currentPage === 'dept'        && <DeptPage go={go} drill={drill} scope={scope} initialGroup={currentEntry.context?.type ? CONTEXT_TYPE_TO_GROUP[currentEntry.context.type] || 'Department' : 'Department'} />}
                        {currentPage === 'sims'        && <SimsPage go={go} drill={drill} openDrawer={openDrawer} period={period} startDate={startDate} endDate={endDate} scope={scope} selfScope={selfTenancyScope} search={search} vis={simsVisibility.vis} orderedGroup={simsVisibility.orderedGroup} dataReady={dataReady} />}
                        {dataReady && currentPage === 'users'       && (() => {
                            const sortValue = pageContextValue('sort');
                            const sortCtx = typeof sortValue === 'string' ? sortValue.split(':') : null;
                            return <UsersPage go={go} drill={drill} openDrawer={openDrawer} scope={scope} initialSortBy={sortCtx?.[0]} initialSortDir={sortCtx?.[1]} period={period} startDate={startDate} endDate={endDate} />;
                        })()}
                        {currentPage === 'courses'     && <CoursesPage go={go} drill={drill} period={period} startDate={startDate} endDate={endDate} scope={scope} selfScope={selfTenancyScope} removeScope={removeScope} search={search} vis={coursesVisibility.vis} orderedGroup={coursesVisibility.orderedGroup} />}
                        {currentPage === 'sim-detail'      && <SimDetailPage go={go} drill={drill} simName={currentData} period={period} startDate={startDate} endDate={endDate} scope={drillScope} selfScope={selfTenancyScope} />}
                        {currentPage === 'course-detail'   && <CourseDetailPage go={go} drill={drill} courseName={currentData} period={period} startDate={startDate} endDate={endDate} scope={drillScope} selfScope={selfTenancyScope} />}
                        {dataReady && currentPage === 'all-sims-table' && <AllSimsPage drill={drill} scope={scope} period={period} startDate={startDate} endDate={endDate} />}
                        {dataReady && currentPage === 'all-courses-table' && <AllCoursesPage drill={drill} scope={scope} period={period} startDate={startDate} endDate={endDate} />}
                        {currentPage === 'user-detail'     && <UserDetailPage go={go} drill={drill} userEmail={currentData} period={period} startDate={startDate} endDate={endDate} scope={drillScope} />}
                        {dataReady && currentPage === 'meta-detail'     && <MetaDetailPage go={go} scope={scope}metaType={currentEntry.context?.type} metaValue={currentEntry.context?.value} period={period} startDate={startDate} endDate={endDate} />}
                        {dataReady && currentPage === 'send-history'     && <SendHistoryPage drill={drill} scope={scope} selfScope={selfTenancyScope} search={search} period={period} startDate={startDate} endDate={endDate} initialStatus={pageContextValue('status')} />}
                        {dataReady && currentPage === 'course-send-log'  && <CourseSendHistoryPage drill={drill} scope={scope} selfScope={selfTenancyScope} search={search} period={period} startDate={startDate} endDate={endDate} initialStatus={pageContextValue('status')} />}
                        {currentPage === 'outstanding-users'&& <OutstandingUsersPage drill={drill} scope={scope} search={search} period={period} startDate={startDate} endDate={endDate} />}
                        {currentPage === 'risk-methodology' && <RiskMethodologyPage />}
                        {currentPage === 'flagged-sims'     && (FEATURES.flaggedSimulations ? <FlaggedSimsPage drill={drill} scope={scope} period={period} startDate={startDate} endDate={endDate} /> : <FeatureOffNotice feature="Flagged simulations" />)}
                        {dataReady && currentPage === 'dwm'              && (FEATURES.darkWebMonitoring   ? <DwmPage drill={drill} startDate={startDate} endDate={endDate} /> : <FeatureOffNotice feature="Dark web monitoring" />)}
                        {dataReady && currentPage === 'export-builder'   && (FEATURES.exportBuilder       ? <ExportBuilderPage initialScope={scope} /> : <FeatureOffNotice feature="Export builder" />)}
                        {dataReady && currentPage === 'export-scheduled' && (FEATURES.exportBuilder       ? <ScheduledExportsPage />            : <FeatureOffNotice feature="Export builder" />)}
                        {dataReady && currentPage === 'export-tables'    && (FEATURES.exportBuilder       ? <DataTablesPage scope={scope} />    : <FeatureOffNotice feature="Export builder" />)}
                        {dataReady && currentPage === 'export-history'   && (FEATURES.exportBuilder       ? <ExportHistoryPage />               : <FeatureOffNotice feature="Export builder" />)}
                        {dataReady && currentPage === 'meta-list'        && <MetaListPage drill={drill} scope={scope} period={period} startDate={startDate} endDate={endDate} metaType={metaList.pivot?.context.value} originPage={metaList.origin || { id: 'sims', label: 'Simulations' }} />}
                        {currentPage === 'factor-detail' && (
                            <IgnoreExcludeDisabled.Provider value>
                                <FactorDetailPage drill={drill} scope={scope} factorId={currentEntry.context?.value} />
                            </IgnoreExcludeDisabled.Provider>
                        )}
                        {performance && <PerformancePage key={performance.kind} kind={performance.kind} initialWidgetId={performance.widgetId} go={go} drill={drill} scope={scope} period={period} startDate={startDate} endDate={endDate} />}
                </motion.div>
                </PageErrorBoundary>
                )}
                </HydratingContext.Provider>

                {(() => {
                    const VIS_BY_PAGE = {
                        summary: summaryVisibility,
                        sims: simsVisibility,
                        courses: coursesVisibility,
                    };
                    const v = VIS_BY_PAGE[currentPage];
                    if (!v) return null;
                    return (
                        <WidgetPanel
                            open={customiseOpen}
                            onClose={() => { v.flushSave?.(); setCustomiseOpen(false); }}
                            allWidgets={v.allWidgets}
                            hidden={v.hidden}
                            onToggle={v.toggle}
                            onRestoreAll={v.restoreAll}
                            page={currentPage}
                            customTemplates={v.customTemplates}
                            onSaveTemplate={v.saveTemplate}
                            onDeleteTemplate={v.deleteTemplate}
                        />
                    );
                })()}

                <Drawer
                    item={drawerOpen ? drawerItem : null}
                    type={drawerType}
                    onClose={closeDrawer}
                    go={go}
                />

                {/* FeatureSettings dialog mount removed: feature flags
                    are administered from the portal Features page so the
                    tenancy admin only ever uses one surface. */}
                <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} go={go} drill={drill} dataVersion={dataVersion} dimOptions={dimOptions} userSearchScope={selfTenancyScope} />
            </div>
        </ThemeProvider>
        </ReportFiltersContext.Provider>
    );
}
